import React, { useState, useRef, useCallback } from 'react';
import { generateVideoWithWanAnimate } from '../services/geminiService';
import { generateVideoWithWan22Animate } from '../services/falService';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { CloseIcon } from './icons/CloseIcon';
import { UploadIcon } from './icons/UploadIcon';
import { SaveIcon } from './icons/SaveIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { CREDIT_COSTS } from '../types';

export const AnimateVideoModal = ({ shot, projectData, onClose, onSave, onDeductCredits, onTrackCreditsUsed }) => {
    const [referenceVideo, setReferenceVideo] = useState(null);
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
    const [status, setStatus] = useState('idle');
    const [progressMessage, setProgressMessage] = useState('');
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [animationPrompt, setAnimationPrompt] = useState(
        shot?.description || 'Animate this image with natural, fluid motion'
    );
    const fileInputRef = useRef(null);
    const [resolution, setResolution] = useState(
        projectData?.modelPreferences?.animate?.wan22Animate?.resolution || '480p'
    );

    const handleFile = (file) => {
        if (file && file.type.startsWith('video/')) {
            const MAX_SIZE_MB = 7;
            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                setError(`Reference video is too large. The API has a 10MB limit; please upload a file smaller than ${MAX_SIZE_MB}MB.`);
                return;
            }
            setError(null);

            const reader = new FileReader();
            reader.onloadend = () => {
                setReferenceVideo({
                    name: file.name,
                    data: reader.result,
                });
            };
            reader.readAsDataURL(file);
        } else {
            setError("Invalid file type. Please upload a video file.");
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    }, []);

    const handleDragEvents = (e, isEntering) => {
        e.preventDefault();
        e.stopPropagation();
        if (isEntering) setIsDragging(true);
        else setIsDragging(false);
    };
    
    const handleGenerate = async () => {
        if (!shot?.startFrameImage) {
            setError("A start frame image is required. Please generate or upload a start frame first.");
            return;
        }
        
        // Credits are deducted by backend - removed upfront deduction

        setStatus('generating');
        setError(null);
        setProgressMessage('Starting Wan 2.2 animation...');

        try {
            // Use Wan 2.2 directly for image-to-video animation
            const result = await generateVideoWithWan22Animate(
                animationPrompt,
                shot.startFrameImage,
                setProgressMessage,
                projectData?.aspectRatio || '16:9',
                resolution
            );
            
            // Handle both old format (string) and new format (object with credits)
            const videoUrl = result?.videoUrl || result;
            setGeneratedVideoUrl(videoUrl);
            setStatus('done');
            
            // Track credits from backend response
            if (onTrackCreditsUsed && result?.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred during animation.";
            setError(errorMessage);
            setStatus('error');
        }
    };

    const handleSaveToProject = () => {
        if (generatedVideoUrl) {
            onSave(shot.id, generatedVideoUrl);
        }
    };

    const handleDownload = async () => {
        if (!generatedVideoUrl) return;
        setError(null);
        try {
            const response = await fetch(generatedVideoUrl);
            if (!response.ok) throw new Error('Failed to fetch video data for download.');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = `loopai_animation_${shot.id}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(objectUrl);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "An unknown error occurred during download.";
            console.error("Download failed:", e);
            setError(`Video download failed. ${errorMessage}`);
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} role="dialog">
            <div className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <header className="p-4 flex justify-between items-center border-b border-gray-700 shrink-0">
                    <h2 className="text-xl font-bold">Animate Shot with Wan 2.2</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700"><CloseIcon className="w-6 h-6" /></button>
                </header>

                <main className="p-6 flex-grow overflow-y-auto">
                    {status === 'generating' ? (
                        <div className="text-center h-64 flex flex-col justify-center">
                            <LoadingSpinner className="w-12 h-12 mx-auto mb-4" />
                            <h3 className="text-2xl font-semibold mb-2">Animating with Wan 2.2...</h3>
                            <p className="text-purple-400">{progressMessage}</p>
                        </div>
                    ) : status === 'done' && generatedVideoUrl ? (
                         <div className="space-y-4">
                            <h3 className="text-xl font-semibold text-center text-green-400">Animation Complete!</h3>
                            <video src={generatedVideoUrl} controls autoPlay loop className="w-full rounded-lg" />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="font-semibold mb-2">Source Image</h3>
                                    <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
                                        {shot?.startFrameImage ? (
                                            <img src={shot.startFrameImage} alt="Start frame" className="w-full h-full object-contain rounded-lg" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                        ) : (
                                            <p className="text-gray-500 text-sm">No start frame image available</p>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="font-semibold mb-2">Reference Motion Video (Optional)</h3>
                                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="video/*" className="hidden" />
                                    {referenceVideo ? (
                                        <div className="space-y-2">
                                            <video src={referenceVideo.data} controls className="w-full rounded-lg aspect-video object-contain bg-black" />
                                            <button onClick={() => setReferenceVideo(null)} className="w-full text-sm py-2 bg-gray-600 hover:bg-gray-500 rounded-md">Remove reference video</button>
                                        </div>
                                    ) : (
                                        <div
                                            onDrop={handleDrop}
                                            onDragOver={e => handleDragEvents(e, true)}
                                            onDragEnter={e => handleDragEvents(e, true)}
                                            onDragLeave={e => handleDragEvents(e, false)}
                                            onClick={() => fileInputRef.current?.click()}
                                            className={`aspect-video border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-gray-500'}`}
                                        >
                                            <UploadIcon className="w-10 h-10 text-gray-500 mb-2" />
                                            <p className="text-gray-400 text-sm">Drag & drop video (optional)</p>
                                            <p className="text-gray-500 text-xs">or click to browse</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Animation Prompt */}
                            <div>
                                <h3 className="font-semibold mb-2">Animation Prompt</h3>
                                <textarea
                                    value={animationPrompt}
                                    onChange={(e) => setAnimationPrompt(e.target.value)}
                                    placeholder="Describe how you want the image to be animated..."
                                    rows={3}
                                    className="w-full p-3 bg-gray-700/60 border border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                />
                            </div>
                            
                            {/* Resolution Selection */}
                            <div>
                                <h3 className="font-semibold mb-2">Resolution</h3>
                                <select
                                    value={resolution}
                                    onChange={(e) => setResolution(e.target.value)}
                                    className="w-full p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="480p">480p (Fastest)</option>
                                    <option value="580p">580p (Balanced)</option>
                                    <option value="720p">720p (Best Quality)</option>
                                </select>
                                <p className="text-xs text-center text-gray-400 mt-2">
                                    Wan 2.2 will generate approximately 4-5 seconds of animation from your image.
                                </p>
                            </div>
                        </div>
                    )}
                    {error && <div className="mt-4 bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm text-center">{error}</div>}
                </main>
                
                <footer className="p-4 flex justify-between items-center border-t border-gray-700 shrink-0">
                    <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 font-bold py-2 px-4 rounded-lg">Close</button>
                    <div className="flex items-center gap-4">
                        {status === 'done' && (
                            <>
                                <button onClick={handleDownload} className="bg-sky-600 hover:bg-sky-700 font-bold py-2 px-4 rounded-lg flex items-center"><DownloadIcon className="w-5 h-5 mr-2"/> Download</button>
                                <button onClick={handleSaveToProject} className="bg-green-600 hover:bg-green-700 font-bold py-2 px-4 rounded-lg flex items-center"><SaveIcon className="w-5 h-5 mr-2"/> Save to Project</button>
                            </>
                        )}
                        <button
                            onClick={handleGenerate}
                            disabled={!shot?.startFrameImage || status === 'generating'}
                            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 font-bold py-2 px-4 rounded-lg flex items-center"
                        >
                            <MagicWandIcon className="w-5 h-5 mr-2"/>
                            {status === 'done' ? 'Re-generate' : 'Animate Video'}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};
